import { useEffect, useRef, useState } from "react";
import {
  type ColDef,
  type ColGroupDef,
  type SortChangedEvent,
} from "ag-grid-community";

import { type ApiGatewayWorkbenchApiKey } from "@vega-api/generated/types/apiGatewayWorkbenchApiKey";
import { DataGrid } from "@vega-core/components/DataGrid/DataGrid";
import { RowsLoading } from "@vega-core/components/Table/RowsLoading";
import { Tag } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";

import { KeyStatus } from "./deriveKeyStatus";

const STATUS_LABEL: Record<KeyStatus, () => string> = {
  [KeyStatus.Active]: m.settings_reportingApiKeys_status_active,
  [KeyStatus.Expired]: m.settings_reportingApiKeys_status_expired,
  [KeyStatus.Deactivated]: m.settings_reportingApiKeys_status_deactivated,
};

const STATUS_VARIANT: Record<KeyStatus, "Negative" | "Positive" | "Warning"> = {
  [KeyStatus.Active]: "Positive",
  [KeyStatus.Expired]: "Warning",
  [KeyStatus.Deactivated]: "Negative",
};

export const KeyStatusTag = ({ status }: { status: KeyStatus }) => (
  <Tag variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]()}</Tag>
);

export type KeySort = {
  sortBy: string;
  sortDirection: "ASC" | "DESC";
};

/** Table column ids -> the contract's sortBy enum values. */
const SORT_BY_COLUMN: Record<string, string> = {
  keyName: "KEY_NAME",
  userName: "USER_NAME",
  organizationName: "ORGANIZATION_NAME",
  status: "STATUS",
  createdAt: "CREATED_AT",
  expiresAt: "EXPIRES_AT",
  lastUsedAt: "LAST_USED_AT",
};

const DEFAULT_COL_DEF: ColDef<ApiGatewayWorkbenchApiKey> = {
  sortable: true,
  resizable: true,
  suppressMovable: true,
  flex: 1,
  minWidth: 140,
};

// Skeleton width during the cold load: the seven data columns + actions.
const SKELETON_COLUMNS = 8;

type ApiKeysTableProps = {
  columns: (
    | ColDef<ApiGatewayWorkbenchApiKey>
    | ColGroupDef<ApiGatewayWorkbenchApiKey>
  )[];
  keys: ApiGatewayWorkbenchApiKey[];
  isLoading: boolean;
  paginationElement: React.ReactNode;
  onSortChange: (sort: KeySort | null) => void;
};

export const ApiKeysTable = ({
  columns,
  keys,
  isLoading,
  paginationElement,
  onSortChange,
}: ApiKeysTableProps) => {
  // Treat every mount like a cold load: show the skeleton until this mount's
  // first load settles, so ag-grid mounts once against stable data.
  const [hasLoadedThisMount, setHasLoadedThisMount] = useState(
    () => !isLoading,
  );

  useEffect(() => {
    if (!isLoading) setHasLoadedThisMount(true);
  }, [isLoading]);

  // ag-grid fires sortChanged for non-user sources too (e.g. column
  // visibility clearing a hidden column's sort). Track the last emitted sort
  // and short-circuit no-op events so we don't refetch for nothing.
  const lastEmittedSortRef = useRef<KeySort | null>(null);

  const handleSortChanged = (
    event: SortChangedEvent<ApiGatewayWorkbenchApiKey>,
  ) => {
    const sortedColumn = event.api
      .getColumnState()
      .find((column) => column.sort);
    const sortBy = sortedColumn
      ? SORT_BY_COLUMN[sortedColumn.colId]
      : undefined;

    const next: KeySort | null =
      sortedColumn && sortBy !== undefined
        ? {
            sortBy,
            sortDirection: sortedColumn.sort === "asc" ? "ASC" : "DESC",
          }
        : null;

    if (
      lastEmittedSortRef.current?.sortBy === next?.sortBy &&
      lastEmittedSortRef.current?.sortDirection === next?.sortDirection
    ) {
      return;
    }

    lastEmittedSortRef.current = next;
    onSortChange(next);
  };

  // Cold load renders the app's standard skeleton rows (same treatment as
  // CoreTable surfaces) instead of ag-grid's own overlay; the grid mounts
  // once, against the first settled data delivery. Subsequent param changes
  // keep the previous rows via the query's placeholderData, so the grid
  // never flips back to a loading state mid-interaction.
  if (!hasLoadedThisMount) {
    return (
      <div
        className="border border-border/primary rounded-containerCorner overflow-hidden"
        data-testid="reporting-api-keys-loading"
      >
        <table className="w-full">
          <RowsLoading columnsNumber={SKELETON_COLUMNS} />
        </table>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="reporting-api-keys-table">
      <DataGrid<ApiGatewayWorkbenchApiKey>
        columnDefs={columns}
        defaultColDef={DEFAULT_COL_DEF}
        emptyState={{
          title: m.settings_reportingApiKeys_emptyState_title(),
          description: m.settings_reportingApiKeys_emptyState_description(),
        }}
        // Admins copy key names / user ids straight from the listing; ag-grid
        // suppresses browser text selection unless asked not to.
        enableCellTextSelection
        getRowId={(params) => `${params.data.userId}:${params.data.keyName}`}
        onSortChanged={handleSortChanged}
        rowData={keys}
        // ag-grid initialises cells on animation frames, which browsers freeze
        // in background tabs — a page loaded in a hidden tab would show blank
        // cells until the next grid interaction. This listing renders at most
        // one page of rows, so synchronous cell rendering is a safe trade.
        suppressAnimationFrame
      />
      {paginationElement}
    </div>
  );
};
